import type { Router } from 'vue-router';
import { Home, LayoutGrid, Settings } from '@lucide/vue';
import type { NxCommand } from '@blpde/nx-ui';
import { apps, appAllowed } from '@/integration';
import { appTranslator } from '@/i18n/appI18n';

/**
 * Context of the shell's command building. `router` for navigation, `t` so
 * labels/hints localize through the shell's i18n.
 */
export interface CommandContext {
  router: Router;
  t: (key: string, named?: Record<string, unknown>) => string;
  /** Optional shell permission check used to omit unauthorized app commands. */
  can?: (requirement: string | string[], mode?: 'all' | 'any') => boolean;
}

/**
 * What a module's command factory gets: `t` over the module's own texts — the
 * shell keeps them per app, so another app's texts are out of reach (I-2284) —
 * and the module's name for labels like „ERP: Angebote“.
 */
export interface ModuleCommandContext extends CommandContext {
  /** The app's name in the shell's language. */
  appLabel: string;
}

/**
 * A module's command contribution — the command-palette equivalent of `nav`.
 * Each hosted module exposes one of these on its `AppEntry.commands`; the shell
 * fans out over all of them (see `buildModuleCommands`). Returning [] (or not
 * setting `commands` at all) is fine — the module still gets its auto-generated
 * "open" command from `buildGlobalCommands`.
 */
export type CommandFactory = (ctx: ModuleCommandContext) => NxCommand[];

/**
 * Shell-owned global commands: jump to the overview + an "open <app>" entry
 * auto-derived from the app registry. Adding an app to `apps` therefore adds
 * its open-command for free — nothing to edit here.
 */
export function buildGlobalCommands(ctx: CommandContext): NxCommand[] {
  const { router, t } = ctx;
  const general = t('connect.command.general');
  return [
    {
      id: 'connect.home',
      label: t('connect.rail.home'),
      group: general,
      icon: Home,
      keywords: ['home', 'dashboard', 'start', 'übersicht', 'overview'],
      run: () => {
        void router.push('/');
      },
    },
    {
      id: 'connect.settings',
      label: t('connect.menu.settings'),
      group: general,
      icon: Settings,
      keywords: ['settings', 'preferences', 'einstellungen', 'profile', 'profil'],
      run: () => {
        void router.push('/settings');
      },
    },
    {
      id: 'connect.showcase',
      label: t('connect.command.showcase'),
      group: general,
      icon: LayoutGrid,
      keywords: ['showcase', 'komponenten', 'components', 'ui-kit', 'kit', 'galerie', 'gallery'],
      run: () => {
        void router.push('/showcase');
      },
    },
    ...apps
      .filter((app) => !ctx.can || appAllowed(app, ctx.can))
      .map((a): NxCommand => ({
      id: `connect.app.${a.id}`,
      label: t('connect.command.open', { name: t(a.labelKey) }),
      group: general,
      icon: a.icon,
      keywords: [a.id, t(a.labelKey)],
      run: () => {
        void router.push(a.basePath);
      },
      })),
  ];
}

/**
 * Every hosted module's own commands, flattened. Each AppEntry may expose a
 * `commands` factory; this fans out over all of them. This is the seam that
 * lets a module inject palette entries beyond its plain "open" command.
 */
export function buildModuleCommands(ctx: CommandContext): NxCommand[] {
  return apps
    .filter((app) => !ctx.can || appAllowed(app, ctx.can))
    .flatMap(
      (a) => a.commands?.({ ...ctx, t: appTranslator(a.id), appLabel: ctx.t(a.labelKey) }) ?? []
    );
}
